"use client";
import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { SERVICE_AREA_LINKS } from '@shared/serviceAreaPages';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { id: "residential", label: "Residential", href: "/residential" },
    { id: "commercial", label: "Commercial", href: "/commercial" },
    { id: "plans", label: "EZ Maintenance Plans", href: "/maintenance-plans" },
    { id: "blog", label: "Blog", href: "/blog" },
    { id: "guides", label: "Guides", href: "/guides" },
    { id: "faq", label: "FAQs", href: "/faq" },
    { id: "about", label: "About Us", href: "/about-us" },
    { id: "contact", label: "Contact", href: "/#contact-section" },
  ];

  return (
    <>
      {/* Header Stack Container - Single sticky */}
      <div className="sticky top-0 z-50">
        {/* Marketing Banner */}
        <div className="bg-accent text-accent-foreground py-3 px-3 sm:py-2 sm:px-4 text-xs sm:text-sm text-center font-semibold shadow-md">
          <div className="container">
            <p className="leading-snug">Licensed and insured septic pros servicing the Carolinas. Septic installation, pump-out, repair, and inspection with one EZ call.</p>
          </div>
        </div>

        {/* Top Bar with Contact Info */}
        <div className="bg-primary text-primary-foreground py-2 px-4 text-sm shadow-md">
          <div className="container flex justify-between items-center gap-3 md:gap-6 flex-nowrap">
            <div className="flex items-center gap-2 md:gap-6 justify-start md:justify-center flex-1 min-w-0">
              <a href="tel:9809227867" className="hover:opacity-80 transition text-xs sm:text-sm whitespace-nowrap">
                📞 980-922-PUMP
              </a>
              <a href="mailto:service@EZsepticPros.com" className="hover:opacity-80 transition text-xs sm:text-sm hidden sm:inline whitespace-nowrap">
                ✉️ service@EZsepticPros.com
              </a>
              <a href="/careers" className="hover:opacity-80 transition text-xs sm:text-sm hidden md:inline whitespace-nowrap">Join Our Team</a>
            </div>
            <a href="/schedule" className="bg-accent text-accent-foreground px-3 sm:px-4 py-1.5 rounded-md font-semibold text-xs sm:text-sm hover:bg-accent/90 transition whitespace-nowrap flex-shrink-0">Request Service</a>
          </div>
        </div>

      {/* Main Navigation */}
      <nav className="bg-white border-b border-border shadow-md">
        <div className="w-full">
          <div className="flex items-center justify-between h-24 md:h-28 px-4 md:px-6">
            {/* Logo with Animated Badge */}
            <div className="flex-shrink-0 relative group">
              <a href="/" className="flex items-center gap-2 relative">
                {/* Animated background glow */}
                <div className="absolute inset-0 bg-accent/30 rounded-lg blur opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-150"></div>
                <img
                  src="/manus-storage/logo-nav-128-v2_134b9b63_b75821f5.webp"
                  srcSet="/manus-storage/logo-nav-128-v2_134b9b63_b75821f5.webp 128w, /manus-storage/logo-nav-192-v2_7683b284_2fae8105.webp 192w, /manus-storage/logo-nav-256-v2_2d9538f9_816bbace.webp 256w"
                  sizes="(min-width: 768px) 128px, 96px"
                  alt="EZ Septic Pros Logo"
                  width={128}
                  height={128}
                  decoding="async"
                  className="h-24 md:h-32 w-auto relative z-10 transition-transform group-hover:scale-110 duration-300"
                />
              </a>
            </div>

            {/* Desktop Menu - Direct Links (hidden on mobile), centered */}
            <div className="hidden xl:flex flex-1 items-center justify-end gap-3 2xl:gap-5">
              <DropdownMenu><DropdownMenuTrigger asChild><button className="inline-flex items-center gap-1 text-sm 2xl:text-base font-medium hover:text-primary focus-visible:outline-2 focus-visible:outline-primary">Service Areas <ChevronDown className="size-4" aria-hidden="true" /></button></DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-56"><DropdownMenuItem asChild><a href="/service-areas">All service areas</a></DropdownMenuItem><DropdownMenuSeparator />{SERVICE_AREA_LINKS.map(area => <DropdownMenuItem asChild key={area.slug}><a href={area.href}>{area.county} County</a></DropdownMenuItem>)}</DropdownMenuContent>
              </DropdownMenu>
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  className="text-foreground hover:text-primary transition font-medium text-sm 2xl:text-base"
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="xl:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="xl:hidden pb-4 space-y-3 border-t border-border pt-4 max-h-[65vh] overflow-y-auto">
              <details className="mx-4 rounded-md border border-border"><summary className="py-3 px-3 font-semibold cursor-pointer">Service Areas</summary><ul className="px-3 pb-3 space-y-1"><li><a href="/service-areas" className="block py-2 underline">All service areas</a></li>{SERVICE_AREA_LINKS.map(area => <li key={area.slug}><a href={area.href} className="block py-2 text-sm hover:text-primary">{area.county} County</a></li>)}</ul></details>
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  className="block py-2 px-4 text-foreground hover:bg-accent hover:text-accent-foreground rounded transition"
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>
      </div>
    </>
  );
}
